import { style, globalStyle } from '@vanilla-extract/css';
import { recipe } from '@vanilla-extract/recipes';
import { vars } from '../../theme/contract.css';
import { mobile, reducedMotion } from '../../theme/media';

// ============================================
// TYPES
// ============================================

export type SelectSize = 'sm' | 'md' | 'lg';

/**
 * glass = 기본(liquid glass 표면). flat = 운영자 콘솔(HR·Finance·CEO·SysAdmin) 절제 모티프 —
 * 평면 배경·hairline·radius 0, backdropFilter·glow·그라디언트 밑줄 없음.
 * 계약: docs/plan/operator-console-restraint-atoms-plan.md
 */
export type SelectVariant = 'glass' | 'flat';

// ============================================
// CONTAINER
// ============================================

export const container = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.sm,
});

export const containerFullWidth = style({
  width: '100%',
});

/** 밑줄은 라벨을 긋는다 — 근거는 `Input.css.ts` 의 같은 자리. */
export const label = style({
  position: 'relative',
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.medium,
  color: vars.typography.textColor.secondary,
  letterSpacing: vars.typography.letterSpacing.wide,
  alignSelf: 'flex-start',
  paddingBottom: '6px',
  paddingRight: '14px',
});

export const labelUnderline = style({
  position: 'absolute',
  bottom: 0,
  left: 0,
  height: '1px',
  width: '100%',
  background: `linear-gradient(90deg, ${vars.bright.primary.default} 0%, transparent 100%)`,
});

export const errorMessage = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.stateColor.error.popoverText,
  fontWeight: vars.typography.fontWeight.regular,
  letterSpacing: vars.typography.letterSpacing.wide,
  marginTop: '4px',
});

// ============================================
// WRAPPER
// ============================================

export const wrapper = style({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
});

export const wrapperFullWidth = style({
  width: '100%',
});

// ============================================
// SELECT RECIPE
// ============================================

const selectBase = {
  appearance: 'none' as const,
  fontFamily: vars.typography.fontFamily.primary,
  fontWeight: vars.typography.fontWeight.regular,
  color: vars.typography.textColor.primary,
  letterSpacing: vars.typography.letterSpacing.wide,
  background: vars.surface.liquidGlass.innerSurface.background,
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  borderRadius: vars.spacing.borderRadius.md,
  outline: 'none',
  cursor: 'pointer',
  width: '100%',
  transition: `border-color ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}, box-shadow ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  boxShadow: 'none',
  backdropFilter: vars.surface.liquidGlass.backdropFilter.light,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14'%3E%3Cpath fill='none' stroke='%23999' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M3.5 5.5L7 9l3.5-3.5'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
  },
} as const;

export const selectRecipe = recipe({
  base: selectBase,

  variants: {
    selectSize: {
      sm: {
        height: '40px',
        padding: '10px 36px 10px 14px',
        fontSize: vars.typography.fontSize.sm,
        backgroundPosition: 'right 12px center',
        // 폰 — 16px 미만이면 iOS 가 포커스 때 화면을 확대한다(atoms/__tests__/mobileInputZoom.test.ts)
        '@media': { [mobile]: { fontSize: vars.typography.fontSize.body } },
      },
      md: {
        height: '48px',
        padding: '14px 40px 14px 18px',
        fontSize: vars.typography.fontSize.base,
        backgroundPosition: 'right 14px center',
        '@media': { [mobile]: { fontSize: vars.typography.fontSize.body } },
      },
      lg: {
        height: '56px',
        padding: '18px 44px 18px 22px',
        fontSize: vars.typography.fontSize.md,
        backgroundPosition: 'right 18px center',
      },
    },
    isFocused: {
      true: {
        borderColor: vars.bright.primary.default,
        boxShadow: `0 0 8px ${vars.surface.highlight.medium}`,
      },
    },
    isDisabled: {
      true: {
        opacity: 0.4,
        cursor: 'not-allowed',
        boxShadow: 'none',
      },
    },
    variant: {
      glass: {},
      // backgroundColor(롱핸드)로 둔다 — background 쇼트핸드를 쓰면 base 의 화살표 backgroundImage 가 지워진다.
      // radius 는 절제의 표식이 아니라 위계 축이라 base 의 md 를 그대로 상속한다
      // (DESIGN.md §Atom Standards 1-1, 2026-08-29).
      flat: {
        backgroundColor: vars.surface.background.secondary,
        border: `1px solid ${vars.surface.border.secondary}`,
        backdropFilter: 'none',
      },
    },
  },

  compoundVariants: [
    {
      // flat 의 focus 는 테두리 색만 — glow 없음.
      variants: { variant: 'flat', isFocused: true },
      style: {
        borderColor: vars.bright.primary.default,
        boxShadow: 'none',
      },
    },
  ],

  defaultVariants: {
    selectSize: 'md',
    isFocused: false,
    isDisabled: false,
    variant: 'glass',
  },
});

// Hover state
globalStyle(`${wrapper}:hover select:not(:disabled)`, {
  boxShadow: `0 0 8px ${vars.surface.highlight.medium}`,
  borderColor: vars.bright.primary.default,
});

globalStyle(`${wrapper} select:focus-visible`, {
  outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
  outlineOffset: vars.focus.ring.offset,
  borderColor: vars.bright.primary.default,
  boxShadow: `0 0 10px ${vars.surface.highlight.strong}`,
});

// flat 은 hover/focus 에서도 glow 를 켜지 않는다 — 위 글래스 globalStyle 보다 구체도가 높아(속성 셀렉터) 이긴다.
globalStyle(`${wrapper}:hover select[data-variant='flat']:not(:disabled)`, {
  boxShadow: 'none',
});

globalStyle(`${wrapper} select[data-variant='flat']:focus-visible`, {
  boxShadow: 'none',
});

// Option styling (limited browser support but helps where it works)
globalStyle(`${wrapper} select option`, {
  background: vars.form.optionBackground,
  color: vars.typography.textColor.primary,
  padding: '8px',
});

// ============================================
// ANIMATED BORDER
// ============================================

export const animatedBorder = style({
  display: 'none',
});

export const animatedBorderFocused = style({});
